import { useEffect, useRef, useState } from 'react';
import type { Sessao } from '../../auth';
import { Icone } from '../../design/Icone';
import { registrarFalta } from '../../faltas';
import type { Medalha } from '../../faltas';

/**
 * ENCOMENDAS → REGISTRAR FALTA (dono, 02/10/2026) — UM clique, sem modal, sem formulário, sem
 * "tem certeza?". O botão vira "✓ Falta registrada" na hora (otimista) e fica travado alguns
 * segundos contra clique duplo; o servidor também não duplica. Erro desfaz o "✓" e avisa — o
 * produto pesquisado continua na tela.
 */

const TRAVA_MS = 4_000;
const TOAST_MS = 3_200;
const MEDALHA_MS = 6_500;

type Estado = 'pronto' | 'enviando' | 'registrada' | 'erro';

type ConteudoDoToast =
  | { readonly tipo: 'ok' | 'repetida'; readonly texto: string }
  | { readonly tipo: 'medalha'; readonly medalha: Medalha; readonly validas: number };
type Toast = ConteudoDoToast & { readonly id: number };

export function BotaoRegistrarFalta({
  sessao,
  produtoId,
  consultaId,
  termoBusca,
}: {
  sessao: Sessao;
  produtoId: string;
  consultaId: string;
  termoBusca: string | null;
}) {
  const [estado, setEstado] = useState<Estado>('pronto');
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const seq = useRef(0);
  const vivo = useRef(true);
  const timers = useRef<number[]>([]);
  useEffect(
    () => () => {
      vivo.current = false;
      for (const t of timers.current) window.clearTimeout(t);
    },
    [],
  );
  const depois = (ms: number, f: () => void) => {
    timers.current.push(window.setTimeout(() => vivo.current && f(), ms));
  };
  const mostrar = (t: ConteudoDoToast, ms: number) => {
    seq.current += 1;
    const id = seq.current;
    setToasts((xs) => [...xs.slice(-2), { ...t, id }]);
    depois(ms, () => setToasts((xs) => xs.filter((x) => x.id !== id)));
  };

  const clicar = () => {
    if (estado === 'enviando' || estado === 'registrada') return;
    setEstado('registrada'); // otimista: o balconista já segue o atendimento
    void registrarFalta(sessao, { produtoId, consultaId, termoBusca }).then((r) => {
      if (!vivo.current) return;
      if (!r.ok) {
        setEstado('erro');
        return;
      }
      const f = r.dados.falta;
      if (f.repetida)
        mostrar(
          { tipo: 'repetida', texto: 'Essa falta já estava registrada neste atendimento.' },
          TOAST_MS,
        );
      else mostrar({ tipo: 'ok', texto: 'FALTA REGISTRADA' }, TOAST_MS);
      if (f.campanha?.medalhaNova)
        mostrar(
          { tipo: 'medalha', medalha: f.campanha.medalhaNova, validas: f.campanha.validas },
          MEDALHA_MS,
        );
      depois(TRAVA_MS, () => setEstado('pronto'));
    });
  };

  const registrada = estado === 'registrada';
  return (
    <>
      <button
        type="button"
        className={`enc-falta${registrada ? ' enc-falta-feita' : ''}`}
        onClick={clicar}
        disabled={registrada}
        aria-live="polite"
      >
        <Icone nome={registrada ? 'check' : 'falta'} tamanho={18} traco={1.9} />
        {registrada ? 'FALTA REGISTRADA' : 'FALTA'}
      </button>
      {estado === 'erro' && (
        <p className="enc-falta-erro" role="alert">
          Não foi possível registrar a falta. Tente novamente.
        </p>
      )}
      {toasts.length > 0 && (
        <div className="es-toasts enc-falta-toasts" aria-live="polite">
          {toasts.map((t) =>
            t.tipo === 'medalha' ? (
              <div key={t.id} className="es-toast enc-medalha-toast" role="status">
                <span className="enc-medalha-emoji" aria-hidden="true">
                  {t.medalha.emoji || '🏅'}
                </span>
                <span>
                  <b>Nova medalha! {t.medalha.nome}</b>
                  <small>
                    Você ajudou a registrar {String(t.validas)}{' '}
                    {t.validas === 1 ? 'falta' : 'faltas'} e a melhorar o nosso estoque.
                  </small>
                </span>
              </div>
            ) : (
              <div key={t.id} className="es-toast" role="status">
                <Icone nome="check" tamanho={16} traco={1.8} />
                <span>{t.texto}</span>
              </div>
            ),
          )}
        </div>
      )}
    </>
  );
}
